import type { CSSProperties, PropsWithChildren } from 'react';

import type { JSONContent } from '@tiptap/core';

import type { SerializableNode } from '@/serializer';
import type { RSSFeedDataPodcastShow } from '@/types';
import { EmptyState } from '@/ui/EmptyState/EmptyState';
import { useMobileCarouselSettings } from '@/utils';
import { cn } from '@/utils/cn';

import EmblaCarousel from '../components/EmblaCarousel';

import { RSS_FEED_DEFAULTS } from './constants';
import * as styles from './RSSFeed.css';
import type { RSSFeedElementProps } from './RSSFeed.types.js';
import { PodcastSerializer } from './Serializer';

const COLUMNS_MAP: Record<string, string> = {
  '1': 'grid-cols-1',
  '2': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-2',
  '3': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  '4': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  '5': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-5',
  '6': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-6',
};

const OPTIONS: any = {
  align: 'start',
  containScroll: 'trimSnaps',
};

export const PodcastShowEpisodes = (props: PropsWithChildren<RSSFeedElementProps>) => {
  const { element, attributes, children, className, isLive = false, viewport } = props;
  const {
    data: rawData,
    padding,
    gap,
    borderRadius,
    borderWidth,
    borderColor,
    borderStyle,
    isScrollHorizontally,
    isMobileScrollHorizontally,

    // Empty State
    emptyStatePadding,
    emptyStateBackgroundColor,
    emptyStateTextColor,
    emptyStateText,
    emptyStateFontSize,

    structureTiptapState,
  } = element.attrs;

  const episodes = (rawData as RSSFeedDataPodcastShow)?.episodes ?? [];
  const hasNoEpisodes = episodes.length === 0;

  const isViewportMobile = viewport === 'mobile';

  const {
    columns: effectiveColumns,
    shouldScrollHorizontally: displayedShouldScrollHorizontally,
    sliderControlsDisplayType: displayedSliderControlsDisplayType,
    sliderControlsBackgroundColor: displayedSliderControlsBackgroundColor,
    sliderControlsTextColor: displayedSliderControlsTextColor,
    sliderControlsBorderColor: displayedSliderControlsBorderColor,
  } = useMobileCarouselSettings({
    attributes: {
      ...element.attrs,
      shouldScrollHorizontally: isScrollHorizontally,
      mobileShouldScrollHorizontally: isMobileScrollHorizontally,
    },
    isViewportMobile: isViewportMobile || false,
  });

  const desktopSlideSize =
    displayedShouldScrollHorizontally && effectiveColumns ? `${100 / Number.parseInt(effectiveColumns, 10)}%` : '';

  if (!structureTiptapState) {
    return null;
  }

  const cardNode = structureTiptapState?.content?.[0] as JSONContent & SerializableNode;

  return (
    <div className="relative flex flex-col w-full" {...attributes}>
      <div
        className={cn(
          'relative w-full h-fit items-stretch',
          className,
          styles.rssFeedGrid,
          displayedShouldScrollHorizontally ? 'flex' : 'grid',
          !displayedShouldScrollHorizontally && COLUMNS_MAP[effectiveColumns || '3'],
        )}
        style={
          {
            '--card-border-radius': borderRadius || RSS_FEED_DEFAULTS.borderRadius,
            '--card-border-width': borderWidth || RSS_FEED_DEFAULTS.borderWidth,
            '--card-border-color': borderColor || RSS_FEED_DEFAULTS.borderColor,
            '--card-border-style': borderStyle || RSS_FEED_DEFAULTS.borderStyle,
            padding: padding,

            // Slider
            '--gap': gap,

            // Desktop Slider
            '--desktop-slide-size': desktopSlideSize,
            '--post-slider-control-background-color': displayedSliderControlsBackgroundColor,
            '--post-slider-control-text-color': displayedSliderControlsTextColor,
            '--post-slider-control-border-color': displayedSliderControlsBorderColor,

            // Mobile Slider
            '--mobile-slider-control-background-color':
              element.attrs.mobileSliderControlsBackgroundColor || displayedSliderControlsBackgroundColor,
            '--mobile-slider-control-text-color':
              element.attrs.mobileSliderControlsTextColor || displayedSliderControlsTextColor,
            '--mobile-slider-control-border-color':
              element.attrs.mobileSliderControlsBorderColor || displayedSliderControlsBorderColor,
          } as CSSProperties
        }
      >
        <EmblaCarousel
          options={OPTIONS}
          isCarousel={!!displayedShouldScrollHorizontally}
          isLive={isLive}
          controlsDisplayType={displayedSliderControlsDisplayType}
        >
          {episodes.map((episode) => (
            <div
              key={episode.id}
              className={cn('embla__slide podcast-serializer-container relative', isLive && 'cursor-pointer')}
            >
              {isLive ? (
                <a href={episode.url} className="relative z-10 h-full embla__slide__number" target="_blank">
                  <PodcastSerializer
                    node={cardNode}
                    rootClassName="h-full"
                    rootStyle={{}}
                    episodeTitle={episode.title}
                    episodeDescription={episode.description}
                    episodePublished={episode.published}
                    episodeThumbnailUrl={episode.thumbnailUrl}
                    episodeUrl={episode.url}
                    isLive={isLive}
                  />
                </a>
              ) : (
                <PodcastSerializer
                  node={cardNode}
                  rootClassName="h-full"
                  rootStyle={{}}
                  episodeTitle={episode.title}
                  episodeDescription={episode.description}
                  episodePublished={episode.published}
                  episodeThumbnailUrl={episode.thumbnailUrl}
                  episodeUrl={episode.url}
                  isLive={isLive}
                />
              )}
            </div>
          ))}
        </EmblaCarousel>
      </div>
      {hasNoEpisodes ? (
        <EmptyState
          padding={emptyStatePadding}
          backgroundColor={emptyStateBackgroundColor}
          textColor={emptyStateTextColor}
          fontSize={emptyStateFontSize}
        >
          {emptyStateText}
        </EmptyState>
      ) : null}
      {children}
    </div>
  );
};
